import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
    Building2, MapPin, CheckCircle, Loader2, AlertTriangle,
    TrendingDown, ArrowRight, FileText, Home
} from 'lucide-react';

function HomlyLogo() {
    return (
        <div className="font-black text-2xl flex items-center gap-0.5">
            <span className="bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">Homly</span>
        </div>
    );
}

export default function AmendmentConsultationPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [validated, setValidated] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [comment, setComment] = useState('');

    useEffect(() => {
        if (token) {
            loadAmendment();
        } else {
            setError('Token manquant');
            setLoading(false);
        }
    }, [token]);

    const loadAmendment = async () => {
        try {
            const response = await api.get(`/mandates-v2/amendments/consult/${token}`);
            setData(response.data);
            if (response.data.amendment?.status === 'validated') {
                setValidated(true);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Avenant non trouvé');
        } finally {
            setLoading(false);
        }
    };

    const handleValidate = async () => {
        setSubmitting(true);
        try {
            await api.post(`/mandates-v2/amendments/validate/${token}`, null, {
                params: { comment: comment || undefined }
            });
            setValidated(true);
            setTimeout(() => {
                toast.success('Avenant validé avec succès !');
            }, 100);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la validation');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#681793] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
                <AlertTriangle className="w-16 h-16 text-red-400 mb-4" />
                <h1 className="text-2xl font-bold text-white mb-2">Avenant non trouvé</h1>
                <p className="text-muted-foreground mb-6">{error}</p>
                <Link to="/">
                    <Button className="bg-gradient-to-r from-[#681793] to-[#FF914D]">
                        Retour à l'accueil
                    </Button>
                </Link>
            </div>
        );
    }

    const { amendment, mandate, agency } = data;
    const property = mandate?.property || {};

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="bg-slate-900/50 border-b border-border">
                <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
                    <HomlyLogo />
                    {agency?.logo_url && (
                        <img src={agency.logo_url} alt={agency.name} className="h-10 object-contain" />
                    )}
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-8">
                {/* Beta warning */}
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6"
                >
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                        <div>
                            <p className="text-sm font-medium text-amber-400">Avenant - Modification de prix</p>
                            <p className="text-xs text-amber-300/70">
                                En validant cet avenant, vous acceptez la modification du prix de vente de votre bien.
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Already validated */}
                {validated && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="p-8 rounded-2xl bg-green-500/10 border border-green-500/20 mb-6 text-center"
                    >
                        <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-green-400 mb-2">
                            Avenant validé
                        </h2>
                        <p className="text-green-300/70 mb-4">
                            Le nouveau prix de {amendment.new_price?.toLocaleString('fr-FR')} € a été enregistré.
                        </p>
                        <p className="text-sm text-green-300/50">
                            Votre agent immobilier a été notifié de cette validation.
                        </p>
                    </motion.div>
                )}

                {/* Main card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-slate-900 rounded-2xl border border-border overflow-hidden"
                >
                    {/* Header with mandate info */}
                    <div className="bg-gradient-to-r from-[#681793] to-[#FF914D] p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                                <TrendingDown className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-white">Proposition de baisse de prix</h1>
                                <p className="text-muted-foreground">Mandat N° {mandate?.mandate_number}</p>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 space-y-6">
                        {/* Property info */}
                        <div className="flex items-start gap-4 p-4 rounded-xl bg-card">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#681793]/20 to-[#FF914D]/20 flex items-center justify-center">
                                <Home className="w-6 h-6 text-[#FF914D]" />
                            </div>
                            <div>
                                <p className="font-semibold text-white">
                                    {property.property_type === 'appartement' ? 'Appartement' : 
                                     property.property_type === 'maison' ? 'Maison' : 'Bien'}
                                </p>
                                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                    <MapPin className="w-3 h-3" />
                                    {property.address}, {property.postal_code} {property.city}
                                </p>
                                {property.surface_habitable && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {property.surface_habitable} m² • {property.rooms} pièce{property.rooms > 1 ? 's' : ''}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Price change visualization */}
                        <div className="p-6 rounded-xl bg-gradient-to-br from-slate-800 to-slate-700 border border-border">
                            <p className="text-sm font-medium text-muted-foreground mb-4 text-center">Modification proposée</p>
                            
                            <div className="flex items-center justify-center gap-6">
                                {/* Old price */}
                                <div className="text-center">
                                    <p className="text-sm text-muted-foreground mb-1">Prix actuel</p>
                                    <p className="text-2xl font-bold text-muted-foreground line-through">
                                        {amendment.previous_price?.toLocaleString('fr-FR')} €
                                    </p>
                                </div>
                                
                                {/* Arrow */}
                                <div className="flex flex-col items-center">
                                    <ArrowRight className="w-8 h-8 text-[#FF914D]" />
                                    <Badge className="mt-2 bg-red-500/20 text-red-300 border-0">
                                        -{amendment.reduction_percent?.toFixed(1)}%
                                    </Badge>
                                </div>
                                
                                {/* New price */}
                                <div className="text-center">
                                    <p className="text-sm text-muted-foreground mb-1">Nouveau prix</p>
                                    <p className="text-3xl font-black text-green-400">
                                        {amendment.new_price?.toLocaleString('fr-FR')} €
                                    </p>
                                </div>
                            </div>
                            
                            <div className="mt-4 pt-4 border-t border-border text-center">
                                <p className="text-sm text-muted-foreground">
                                    Réduction de <span className="font-bold text-[#FF914D]">{amendment.reduction_amount?.toLocaleString('fr-FR')} €</span>
                                </p>
                            </div>
                        </div>

                        {/* Reason if provided */}
                        {amendment.reason && (
                            <div className="p-4 rounded-xl bg-card border border-border">
                                <p className="text-sm font-medium text-muted-foreground mb-2">Justification de la baisse :</p>
                                <p className="text-sm text-muted-foreground italic">"{amendment.reason}"</p>
                            </div>
                        )}

                        {/* Validation form */}
                        {!validated && (
                            <>
                                {/* Comment */}
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground mb-2 block">
                                        Commentaire (optionnel)
                                    </label>
                                    <Textarea
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        placeholder="Ajouter un commentaire..."
                                        className="bg-card border-border text-white placeholder:text-white/30 resize-none"
                                        rows={3}
                                    />
                                </div>

                                {/* Validate button */}
                                <div className="pt-4">
                                    <Button
                                        onClick={handleValidate}
                                        disabled={submitting}
                                        className="w-full h-14 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-lg font-semibold"
                                    >
                                        {submitting ? (
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        ) : (
                                            <CheckCircle className="w-5 h-5 mr-2" />
                                        )}
                                        Valider la baisse de prix
                                    </Button>
                                    
                                    <p className="text-xs text-white/30 text-center mt-4">
                                        En cliquant, vous acceptez la modification du prix de vente.<br />
                                        Cette action a une valeur contractuelle et modifiera votre mandat.
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </motion.div>

                {/* Agency info */}
                <div className="mt-6 text-center text-sm text-muted-foreground">
                    <p>Avenant proposé par <strong className="text-muted-foreground">{agency?.name}</strong></p>
                    <p className="text-xs mt-2">
                        Document généré le {new Date(amendment.created_at).toLocaleString('fr-FR')}
                    </p>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-border py-8 mt-12">
                <div className="max-w-3xl mx-auto px-4 text-center text-sm text-muted-foreground">
                    <p>Propulsé par <Link to="/" className="text-[#681793] font-medium hover:underline">Homly</Link></p>
                </div>
            </footer>
        </div>
    );
}
